import { supabase } from '../lib/supabase';
import {
  Building2, UserCircle, TrendingUp, StickyNote,
  CalendarClock, Layers, Book, Mail, Package, Linkedin,
  MessageSquare, Paperclip, Send
} from 'lucide-react';
import type { ComponentType } from 'react';
import type { Database } from '../types/supabase';
import type { SidebarContextType } from '../contexts/SidebarContext';
import { emailThreadKey, relationsThreadPath } from '../components/relations/threadKey';
import { humanSize } from '../lib/humanSize';
import { sensitive } from '../lib/presentationMode';

/** Base table names known to the Supabase client (accepted by `.from()`). */
type SearchableTable = keyof Database['public']['Tables'];

/** A global sidebar opener (`openAccountSidebar`, …) — typed against the context so a typo cannot silently do nothing. */
export type SidebarOpenMethod = Extract<keyof SidebarContextType, `open${string}Sidebar`>;

// ── Types ──────────────────────────────────────────────────────────────

export type SearchEntityType =
  | 'crm_account' | 'crm_contact' | 'crm_opportunity' | 'crm_note'
  | 'appointment' | 'onboarding_lead' | 'crm_email' | 'email_attachment'
  | 'kb_article' | 'newsletter' | 'feature' | 'linkedin_post' | 'comment';

export interface GlobalSearchResult {
  id: string;
  entityType: SearchEntityType;
  title: string;
  subtitle: string;
  relevance: number;
  raw: any;
}

export interface GlobalSearchResponse {
  results: Map<SearchEntityType, GlobalSearchResult[]>;
  totalCount: number;
}

// ── Entity configs ─────────────────────────────────────────────────────

interface EntitySearchConfig {
  type: SearchEntityType;
  label: string;
  table: SearchableTable;
  searchColumns: string[];
  selectColumns: string;
  titleField: string | ((row: any) => string);
  subtitleField: string | ((row: any) => string);
  limit: number;
  /**
   * PostgREST `or` expressions ANDed with the text match — e.g. to keep noise
   * out of the results. Each entry is one `.or(...)` call.
   */
  andFilters?: string[];
  /** Which rows survive `limit` when the match is wide (e.g. latest emails first). */
  orderBy?: { column: string; ascending: boolean };
  action:
    | { type: 'sidebar'; method: SidebarOpenMethod }
    | { type: 'navigate'; getPath: (row: any) => string }
    /** The target needs a lookup first (no FK to embed): resolve, then navigate. */
    | { type: 'resolve'; resolvePath: (row: any) => Promise<string | null> };
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

/** Thread of the email an attachment was received in (`document_references.source_id`). */
async function resolveAttachmentThreadPath(emailId: string | null): Promise<string | null> {
  if (!emailId) return null;
  const { data } = await supabase
    .from('crm_emails')
    .select('id, gmail_thread_id')
    .eq('id', emailId)
    .maybeSingle();
  return data ? relationsThreadPath(emailThreadKey(data)) : null;
}

const ENTITY_CONFIGS: EntitySearchConfig[] = [
  {
    type: 'crm_account',
    label: 'Comptes',
    table: 'crm_accounts',
    searchColumns: ['name', 'industry', 'city', 'notes'],
    selectColumns: 'id, name, industry, city',
    titleField: 'name',
    subtitleField: (r) => [r.industry, r.city].filter(Boolean).join(' · ') || '',
    limit: 5,
    action: { type: 'sidebar', method: 'openAccountSidebar' },
  },
  {
    type: 'crm_contact',
    label: 'Contacts',
    table: 'crm_contacts',
    searchColumns: ['first_name', 'last_name', 'email'],
    selectColumns: 'id, first_name, last_name, email, title, account_id',
    titleField: (r) => [r.first_name, r.last_name].filter(Boolean).join(' '),
    subtitleField: (r) => [r.title, r.email].filter(Boolean).join(' · ') || '',
    limit: 5,
    action: { type: 'sidebar', method: 'openContactSidebar' },
  },
  {
    type: 'crm_opportunity',
    label: 'Opportunités',
    table: 'crm_opportunities',
    searchColumns: ['name'],
    selectColumns: 'id, name, status, amount, account_id',
    titleField: 'name',
    subtitleField: (r) => [r.status, r.amount ? sensitive(`${r.amount} €`) : ''].filter(Boolean).join(' · '),
    limit: 5,
    action: { type: 'sidebar', method: 'openOpportunitySidebar' },
  },
  {
    type: 'crm_note',
    label: 'Notes',
    table: 'crm_notes',
    searchColumns: ['title', 'content'],
    selectColumns: 'id, title, content, note_type',
    titleField: (r) => r.title || (r.content || '').substring(0, 60) + ((r.content?.length > 60) ? '...' : ''),
    subtitleField: (r) => r.note_type || '',
    limit: 5,
    // `?note=<id>` is read by NotesPage, which holds the note open even when the
    // sidebar filters (source, type) would keep it out of the list.
    action: { type: 'navigate', getPath: (r) => `/admin/notes?note=${r.id}` },
  },
  {
    type: 'appointment',
    label: 'Rendez-vous',
    table: 'appointments',
    searchColumns: ['title', 'description'],
    selectColumns: 'id, title, start_time, status',
    titleField: 'title',
    subtitleField: (r) => formatDateTime(r.start_time) || r.status || '',
    limit: 5,
    // Open the appointment itself: there is no `/admin/appointments/:id` route, and
    // landing on the bare calendar loses the result the user just clicked.
    action: { type: 'sidebar', method: 'openAppointmentSidebar' },
  },
  {
    type: 'onboarding_lead',
    label: 'Leads',
    table: 'onboarding_leads',
    searchColumns: ['company_name', 'contact_first_name', 'contact_last_name', 'contact_email'],
    selectColumns: 'id, company_name, contact_first_name, contact_last_name, contact_email',
    titleField: 'company_name',
    subtitleField: (r) => [r.contact_first_name, r.contact_last_name].filter(Boolean).join(' ') || r.contact_email || '',
    limit: 5,
    action: { type: 'sidebar', method: 'openLeadSidebar' },
  },
  {
    type: 'crm_email',
    label: 'Emails',
    table: 'crm_emails',
    // Same columns as the Relations inbox server search; `body_text` carries a
    // trigram index for this exact `ILIKE '%…%'`.
    searchColumns: ['subject', 'from_email', 'body_text'],
    selectColumns: 'id, subject, from_email, sent_at, direction, gmail_thread_id',
    titleField: (r) => r.subject || '(sans objet)',
    subtitleField: (r) => [r.direction === 'outbound' ? '→ envoyé' : r.from_email, formatDateTime(r.sent_at)].filter(Boolean).join(' · '),
    limit: 5,
    // Newsletters / no-reply already sorted out by the triage never resurface here.
    andFilters: ['disposition.is.null,disposition.neq.ignored'],
    orderBy: { column: 'sent_at', ascending: false },
    action: { type: 'navigate', getPath: (r) => relationsThreadPath(emailThreadKey(r)) },
  },
  {
    type: 'email_attachment',
    label: 'Pièces jointes',
    table: 'document_references',
    // Filename only: text extraction (PLAN-EMAIL-ATTACHMENTS phase B) is not built yet.
    searchColumns: ['filename'],
    selectColumns: 'id, filename, mime_type, size_bytes, source_kind, source_id, created_at',
    titleField: (r) => r.filename || '(sans nom)',
    subtitleField: (r) => [humanSize(r.size_bytes), formatDateTime(r.created_at)].filter(Boolean).join(' · '),
    limit: 5,
    andFilters: ['source_kind.eq.email', 'is_inline.eq.false'],
    orderBy: { column: 'created_at', ascending: false },
    // `source_id` has no FK to `crm_emails` (polymorphic source), so the thread
    // cannot be embedded in the select: look the email up, then open its thread.
    action: { type: 'resolve', resolvePath: (r) => resolveAttachmentThreadPath(r.source_id) },
  },
  {
    type: 'kb_article',
    label: 'Articles',
    table: 'kb_articles',
    // `body_text`: computed field = the body the reader sees (sections first), not the
    // legacy `content` column (migration kb_article_body_text).
    searchColumns: ['title', 'body_text'],
    selectColumns: 'id, title, type, is_published',
    titleField: 'title',
    subtitleField: (r) => [r.type, r.is_published ? 'Publié' : 'Brouillon'].filter(Boolean).join(' · '),
    limit: 5,
    action: { type: 'navigate', getPath: (r) => `/admin/kb/articles/${r.id}` },
  },
  {
    type: 'newsletter',
    label: 'Newsletters',
    table: 'newsletter_campaigns',
    searchColumns: ['name', 'subject'],
    selectColumns: 'id, name, subject, status',
    titleField: 'name',
    subtitleField: (r) => [r.subject, r.status].filter(Boolean).join(' · '),
    limit: 5,
    action: { type: 'navigate', getPath: (r) => `/admin/newsletter/edit/${r.id}` },
  },
  {
    type: 'feature',
    label: 'Fonctionnalités',
    table: 'features',
    searchColumns: ['title', 'description'],
    selectColumns: 'id, title, dev_status, priority',
    titleField: 'title',
    subtitleField: (r) => [r.dev_status, r.priority].filter(Boolean).join(' · '),
    limit: 5,
    action: { type: 'navigate', getPath: (r) => `/admin/features/${r.id}` },
  },
  {
    type: 'linkedin_post',
    label: 'LinkedIn',
    table: 'linkedin_posts',
    searchColumns: ['title', 'content'],
    selectColumns: 'id, title, status, scheduled_for',
    titleField: (r) => r.title || (r.content || '').substring(0, 60) + ((r.content?.length > 60) ? '...' : ''),
    subtitleField: (r) => r.status || '',
    limit: 5,
    action: { type: 'navigate', getPath: (r) => `/admin/linkedin/${r.id}` },
  },
  {
    type: 'comment',
    label: 'Commentaires',
    table: 'comments',
    searchColumns: ['content', 'user_name'],
    selectColumns: 'id, content, user_name, feature_id',
    titleField: (r) => (r.content || '').substring(0, 80) + ((r.content?.length > 80) ? '...' : ''),
    subtitleField: (r) => r.user_name || '',
    limit: 5,
    action: { type: 'navigate', getPath: (r) => `/admin/comments/${r.id}` },
  },
];

// ── Icons & colors per entity ──────────────────────────────────────────

export const ENTITY_ICONS: Record<SearchEntityType, ComponentType<{ className?: string }>> = {
  crm_account: Building2,
  crm_contact: UserCircle,
  crm_opportunity: TrendingUp,
  crm_note: StickyNote,
  appointment: CalendarClock,
  onboarding_lead: Layers,
  crm_email: Mail,
  email_attachment: Paperclip,
  kb_article: Book,
  newsletter: Send,
  feature: Package,
  linkedin_post: Linkedin,
  comment: MessageSquare,
};

export const ENTITY_COLORS: Record<SearchEntityType, string> = {
  crm_account: 'text-emerald-600',
  crm_contact: 'text-violet-600',
  crm_opportunity: 'text-amber-600',
  crm_note: 'text-yellow-600',
  appointment: 'text-cyan-600',
  onboarding_lead: 'text-orange-600',
  crm_email: 'text-rose-600',
  email_attachment: 'text-slate-600',
  kb_article: 'text-teal-600',
  newsletter: 'text-pink-600',
  feature: 'text-indigo-600',
  linkedin_post: 'text-sky-600',
  comment: 'text-neutral-600',
};

export const ENTITY_LABELS: Record<SearchEntityType, string> = Object.fromEntries(
  ENTITY_CONFIGS.map(c => [c.type, c.label])
) as Record<SearchEntityType, string>;

// ── Search function ────────────────────────────────────────────────────

function sanitizeForILIKE(query: string): string {
  return query.replace(/%/g, '\\%').replace(/_/g, '\\_');
}

function getTitle(config: EntitySearchConfig, row: any): string {
  if (typeof config.titleField === 'function') return config.titleField(row);
  return row[config.titleField] || '';
}

function getSubtitle(config: EntitySearchConfig, row: any): string {
  if (typeof config.subtitleField === 'function') return config.subtitleField(row);
  return row[config.subtitleField] || '';
}

function calculateRelevance(query: string, title: string): number {
  const q = query.toLowerCase();
  const t = title.toLowerCase();
  let score = 0;
  if (t === q) score += 30;
  else if (t.startsWith(q)) score += 20;
  else if (t.includes(q)) score += 10;
  else score += 5;
  // Shorter titles rank higher
  score -= Math.log(Math.max(t.length, 1)) * 0.5;
  return Math.max(0, score);
}

export async function globalSearch(query: string): Promise<GlobalSearchResponse> {
  if (!query || query.trim().length < 2) {
    return { results: new Map(), totalCount: 0 };
  }

  const sanitized = sanitizeForILIKE(query.trim());

  const promises = ENTITY_CONFIGS.map(async (config) => {
    const orFilter = config.searchColumns
      .map(col => `${col}.ilike.%${sanitized}%`)
      .join(',');

    // Successive `.or()` calls are ANDed by PostgREST.
    let request = supabase.from(config.table).select(config.selectColumns).or(orFilter);
    for (const filter of config.andFilters ?? []) {
      request = request.or(filter);
    }
    if (config.orderBy) {
      request = request.order(config.orderBy.column, { ascending: config.orderBy.ascending });
    }
    const { data, error } = await request.limit(config.limit);

    if (error) {
      console.warn(`Global search error on ${config.table}:`, error.message);
      return { type: config.type, results: [] as GlobalSearchResult[] };
    }

    const results: GlobalSearchResult[] = (data || []).map((row: any) => {
      const title = getTitle(config, row);
      return {
        id: row.id,
        entityType: config.type,
        title,
        subtitle: getSubtitle(config, row),
        relevance: calculateRelevance(query, title),
        raw: row,
      };
    });

    // Sort by relevance within each entity
    results.sort((a, b) => b.relevance - a.relevance);
    return { type: config.type, results };
  });

  const settled = await Promise.allSettled(promises);

  const resultsMap = new Map<SearchEntityType, GlobalSearchResult[]>();
  let totalCount = 0;

  for (const result of settled) {
    if (result.status === 'fulfilled' && result.value.results.length > 0) {
      resultsMap.set(result.value.type, result.value.results);
      totalCount += result.value.results.length;
    }
  }

  return { results: resultsMap, totalCount };
}

// Export configs for use in action handling
export { ENTITY_CONFIGS };
export type { EntitySearchConfig };
